import React from "react";
import ScheduleWrapper from "./ScheduleWrapper";
import {
  ChipField,
  Datagrid,
  FunctionField,
  ReferenceArrayField,
  ReferenceField,
  SelectField,
  SimpleList,
  SingleFieldList,
  TextField,
} from "react-admin";
import { CourseChoices, FromWeekToWeekChoices, NationalityType, WeekDaysChoices, WeekTypeChoices } from "utils/choices";
import { administrationRequests } from "api/administrationApi";
import RoomField from "server/components/Fields/RoomField";
import { MOBILE_BREAKPOINT, ViewTypeSchedule } from "utils/constants";
import SubstituteProfessorModal from "./SubstituteProfessorModal";
import SecuredComponent from "server/security/SecuredComponent";
import permissions from "app/permissions";
import { employeeRequests } from "api/employeeApi";
import { useMediaQuery } from "@mui/material";
import { settings } from "app";
import { MobileListRow } from "features/MobileListRow";

const ScheduleTable = ({ filters, title, viewType }) => {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);

  return (
    <ScheduleWrapper
      title={title}
      filters={filters}
    >
      {isMobile ? (
        <SimpleList
          linkType={false}
          sx={settings.style.mobileList}
          primaryText={record => (
            <div>
              <MobileListRow
                fields={[{ label: "Ден", value: WeekDaysChoices.find(d => d.id === record.weekDay)?.name }]}
              />
              <MobileListRow
                fields={[
                  {
                    label: "Час",
                    value: `${record.startTime.slice(0, 5)} - ${record.endTime.slice(0, 5)}`,
                  },
                ]}
              />
            </div>
          )}
          secondaryText={record => (
            <div>
              {viewType !== ViewTypeSchedule.Student && (
                <MobileListRow
                  fields={[
                    {
                      label: "Специалности",
                      value: (
                        <ReferenceArrayField
                          source="specialityIds"
                          reference={administrationRequests.getSpecialitiesSelect()}
                        >
                          <SingleFieldList linkType={false}>
                            <ChipField
                              source="name"
                              size="small"
                              variant="outlined"
                            />
                          </SingleFieldList>
                        </ReferenceArrayField>
                      ),
                    },
                  ]}
                />
              )}
              <MobileListRow
                fields={[
                  {
                    label: "Дисциплина",
                    value: (
                      <ReferenceField
                        source="disciplineId"
                        reference={administrationRequests.getDisciplinesSelect()}
                        sortable={false}
                      >
                        <TextField source="name" />
                      </ReferenceField>
                    ),
                  },
                ]}
              />
              <MobileListRow
                fields={[{ label: "Модул", value: record?.curriculumModuleName ?? "-" }]}
              />
              {record.nationality && (
                <MobileListRow
                  fields={[
                    {
                      label: "Националност",
                      value: NationalityType.find(n => n.id === record.nationality)?.name,
                    },
                  ]}
                />
              )}
              <MobileListRow
                fields={[
                  {
                    label: "Тип занятие",
                    value: (
                      <ReferenceField
                        source="classTypeId"
                        reference={administrationRequests.getRoomClassTypesSelect()}
                        sortable={false}
                      >
                        <TextField source="name" />
                      </ReferenceField>
                    ),
                  },
                ]}
              />
              {viewType !== ViewTypeSchedule.Professor && (
                <MobileListRow
                  fields={[
                    {
                      label: "Преподавател",
                      value: (
                        <ReferenceArrayField
                          source="professorsIds"
                          reference={employeeRequests.getEmployeesSelect()}
                          sortable={false}
                        >
                          <SingleFieldList
                            linkType={false}
                            sx={{ display: "flex", flexDirection: "column", gap: 0 }}
                          >
                            <FunctionField render={record => `${record.firstName} ${record.lastName}`} />
                          </SingleFieldList>
                        </ReferenceArrayField>
                      ),
                    },
                  ]}
                />
              )}
              {(viewType === ViewTypeSchedule.Professor || viewType === ViewTypeSchedule.StudyGroup) && (
                <>
                  <MobileListRow
                    fields={[
                      {
                        label: "Зала",
                        value: (
                          <RoomField
                            source="roomId"
                            render={rec => `${rec.roomName} - ${rec.buildingName} (${rec.capacityExam})`}
                          />
                        ),
                      },
                    ]}
                  />
                  <MobileListRow fields={[{ label: "Учебни групи", value: record?.studyGroups }]} />
                </>
              )}
              {viewType === ViewTypeSchedule.Professor && (
                <>
                  <MobileListRow
                    fields={[
                      {
                        label: "Курс",
                        value: CourseChoices.find(option => option.id === record.course)?.name,
                      },
                    ]}
                  />
                  <MobileListRow
                    fields={[
                      {
                        label: "Език на преподаване",
                        value: (
                          <ReferenceField
                            source="instructionLanguageId"
                            reference={administrationRequests.getInstructionLanguagesSelect()}
                          >
                            <TextField source="name" />
                          </ReferenceField>
                        ),
                      },
                    ]}
                  />
                </>
              )}
              <MobileListRow
                fields={[
                  { label: "Тип седмица", value: WeekTypeChoices.find(d => d.id === record.weekType)?.name },
                ]}
              />
              <MobileListRow
                fields={[
                  { label: "От", value: FromWeekToWeekChoices.find(d => d.id === record.fromWeek)?.name },
                  { label: "До", value: FromWeekToWeekChoices.find(d => d.id === record.toWeek)?.name },
                ]}
              />
              {viewType === ViewTypeSchedule.Professor && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  <SecuredComponent permission={permissions.canRequestScheduleProfessorSubstitute}>
                    <SubstituteProfessorModal
                      filters={filters}
                      record={record}
                    />
                  </SecuredComponent>
                </div>
              )}
            </div>
          )}
        />
      ) : (
        <Datagrid
          rowClick={false}
          bulkActionButtons={false}
        >
          <FunctionField
            label="Ден"
            render={record => WeekDaysChoices.find(d => d.id === record.weekDay)?.name}
          />
          <FunctionField
            label="Час"
            render={record => `${record.startTime.slice(0, 5)} - ${record.endTime.slice(0, 5)}`}
          />
          {viewType !== ViewTypeSchedule.Student && (
            <ReferenceArrayField
              source="specialityIds"
              label="Специалности"
              reference={administrationRequests.getSpecialitiesSelect()}
            >
              <SingleFieldList linkType={false}>
                <ChipField
                  source="name"
                  size="small"
                  variant="outlined"
                />
              </SingleFieldList>
            </ReferenceArrayField>
          )}
          <ReferenceField
            label="Дисциплина"
            source="disciplineId"
            reference={administrationRequests.getDisciplinesSelect()}
            sortable={false}
          >
            <TextField
              source="name"
              label="Дисциплина"
            />
          </ReferenceField>
          <TextField
            source="curriculumModuleName"
            label="Модул"
            sortable={false}
            emptyText="-"
          />
          <SelectField
            source="nationality"
            choices={NationalityType}
            label="Националност"
            sortable={false}
            emptyText="-"
          />
          <ReferenceField
            source="classTypeId"
            reference={administrationRequests.getRoomClassTypesSelect()}
            label="Тип занятие"
            sortable={false}
          >
            <TextField source="name" />
          </ReferenceField>
          {viewType !== ViewTypeSchedule.Professor && (
            <ReferenceArrayField
              label="Преподавател"
              source="professorsIds"
              reference={employeeRequests.getEmployeesSelect()}
              sortable={false}
            >
              <SingleFieldList
                linkType={false}
                sx={{ display: "flex", flexDirection: "column", gap: 0 }}
              >
                <FunctionField render={record => `${record.firstName} ${record.lastName}`} />
              </SingleFieldList>
            </ReferenceArrayField>
          )}
          {(viewType === ViewTypeSchedule.Professor || viewType === ViewTypeSchedule.StudyGroup) && [
            <RoomField
              source="roomId"
              label="Зала"
              render={rec => `${rec.roomName} - ${rec.buildingName} (${rec.capacityExam})`}
              sortable={false}
            />,
            <TextField
              source="studyGroups"
              label="Учебни групи"
            />,
          ]}
          {viewType === ViewTypeSchedule.Professor && [
            <FunctionField
              label="Курс"
              render={record => CourseChoices.find(option => option.id === record.course)?.name}
            />,
            <ReferenceField
              source="instructionLanguageId"
              reference={administrationRequests.getInstructionLanguagesSelect()}
              label="Език на преподаване"
            >
              <TextField source="name" />
            </ReferenceField>,
          ]}
          <FunctionField
            label="Тип седмица"
            render={record => WeekTypeChoices.find(d => d.id === record.weekType)?.name}
          />
          <FunctionField
            label="От"
            render={record => FromWeekToWeekChoices.find(d => d.id === record.fromWeek)?.name}
          />
          <FunctionField
            label="До"
            render={record => FromWeekToWeekChoices.find(d => d.id === record.toWeek)?.name}
          />
          {viewType === ViewTypeSchedule.Professor ? (
            <FunctionField
              label=" "
              render={record => (
                <SecuredComponent permission={permissions.canRequestScheduleProfessorSubstitute}>
                  <SubstituteProfessorModal
                    filters={filters}
                    record={record}
                  />
                </SecuredComponent>
              )}
            />
          ) : null}
        </Datagrid>
      )}
    </ScheduleWrapper>
  );
};

export default ScheduleTable;
